<template>

  <v-col cols="12" class="pa-0 d-flex align-center flex-wrap">
    <template v-for="(btn, i) in navButtons" :key="i">
      <!-- gap between page ranges -->
      <span v-if="btn.text" class="px-2 text-no-wrap">{{ btn.text }}</span>
      <span v-else-if="btn.gap" class="px-1">…</span>

      <!-- crawlable: real <a href> -->
      <v-btn v-else-if="isDefaultState && !btn.disabled" :to="pageLink(btn.page)" :rel="btn.rel"
        :aria-current="btn.current ? 'page' : undefined" elevation="0" :variant="btn.current ? 'tonal' : 'text'"
        size="30" :aria-label="btn.label">
        <v-icon v-if="btn.icon">{{ btn.icon }}</v-icon>
        <template v-else>{{ btn.page }}</template>
      </v-btn>

      <!-- reactive fallback -->
      <v-btn v-else @click="tableOptions.page = btn.page" :disabled="btn.disabled"
        :aria-current="btn.current ? 'page' : undefined" elevation="0" :variant="btn.current ? 'tonal' : 'text'"
        size="30" :aria-label="btn.label">
        <v-icon v-if="btn.icon">{{ btn.icon }}</v-icon>
        <template v-else>{{ btn.page }}</template>
      </v-btn>
    </template>

    <span v-if="layoutVersion == 1" class="px-2">
      {{ dataAll.getStatic('product_pag_total', 'total') }} {{ pagData.totalCount }}
    </span>
  </v-col>
</template>

<script setup>


/*
  SEO pagination
  --------------
  Crawlers run JS but don't click, so a button that only changes state gives
  them nothing to follow. Without hard urls page 2 and up don't exist and
  those products never get indexed.

  But too many hard urls is just as bad. Every sort order, page size and filter
  combo would become its own url, all showing the same products in a different
  order. That's duplicate content and wasted crawl budget.

  So two variants per button:
    - isDefaultState → real <a href>, crawler can follow it
    - otherwise      → old reactive way, no url

  A crawler always lands on the clean url, so it always gets the links. Users
  who sort or filter keep the reactive version and those urls stay out of the
  index.
*/

const dataAll = useDataAll()
const route = useRoute()
const { $PAG } = useNuxtApp()
const DEFAULT_ITEMS_PER_PAGE = $PAG.defaultItemsPerPage()

const SIBLINGS = 2  // pages shown either side of the current one

const props = defineProps({
  tableOptions: { type: Object, default: () => ({}) },
  pagData: { type: Object, default: () => ({}) },
  layoutVersion: { type: Number, default: 1 },
})

// check if url is the default state. only page_nr is allowed for default state
const isDefaultState = computed(() => {
  const filterPart = route.path.split('/filter/')[1] ?? ''
  const keys = filterPart.split('/').filter(Boolean).map(s => s.split('=')[0])
  const onlyPaging = keys.every(k => k === 'page_nr')

  return onlyPaging &&
    Number(props.tableOptions.page_size) === DEFAULT_ITEMS_PER_PAGE &&
    !props.tableOptions.sort &&
    !props.tableOptions.direction
})
/**
 * Page numbers to render: always first and last, plus a window around the
 * current page. Gaps in between are marked with null.
 */
const pageWindow = computed(() => {
  const total = totalPages.value
  const current = Number(props.tableOptions.page)

  if (total <= SIBLINGS * 2 + 5) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const left = Math.max(2, current - SIBLINGS)
  const right = Math.min(total - 1, current + SIBLINGS)

  const pages = [1]
  if (left > 2) pages.push(null)
  for (let i = left; i <= right; i++) pages.push(i)
  if (right < total - 1) pages.push(null)
  pages.push(total)

  return pages
})

/** Full button list: first, prev, numbered pages, next, last. */
const navButtons = computed(() => {
  const total = totalPages.value
  const current = Number(props.tableOptions.page)
  const atStart = current <= 1
  const atEnd = current >= total

  return [
    { page: 1, icon: 'mdi-page-first', label: 'Eerste pagina', disabled: atStart },
    { page: current - 1, icon: 'mdi-chevron-left', label: 'Vorige pagina', disabled: atStart, rel: 'prev' },

    ...(props.layoutVersion == 2
      ? pageWindow.value.map((p) =>
        p === null
          ? { gap: true }
          : { page: p, label: `Pagina ${p}`, current: p === current, disabled: p === current }
      )
      : [{ text: `${dataAll.getStatic('page', 'page')} ${current} / ${total}` }]),

    { page: current + 1, icon: 'mdi-chevron-right', label: 'Volgende pagina', disabled: atEnd, rel: 'next' },
    { page: total, icon: 'mdi-page-last', label: 'Laatste pagina', disabled: atEnd },
  ]
})

const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.pagData.totalCount / props.tableOptions.page_size) || 1)
)
// Basepath url withoud filter
const basePath = computed(() =>
  route.path.replace(/\/filter\/page_nr=\d+\/?$/, '') || '/'
)
// url to n-th page, page 1 is no filter
const pageLink = (n) =>
  n === 1 ? basePath.value : `${basePath.value}/filter/page_nr=${n}`
</script>